import React, { useState, useEffect } from 'react';
import { StorageService } from '../services/storageService';

interface SaveOptionsProps {
  topic: string;
  script: string;
  videoLength: string;
  structure: string;
  videoUrl?: string;
}

const SaveOptions: React.FC<SaveOptionsProps> = ({
  topic,
  script,
  videoLength,
  structure,
  videoUrl,
}) => {
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });
  const [autoSavedScript, setAutoSavedScript] = useState(false);
  const [autoSavedVideo, setAutoSavedVideo] = useState(false);

  // Reset auto-save flags when topic changes (new generation)
  useEffect(() => {
    setAutoSavedScript(false);
    setAutoSavedVideo(false);
  }, [topic]);

  // Auto-save when script is generated
  useEffect(() => {
    if (script && !autoSavedScript) {
      // Small delay to ensure script is fully rendered
      setTimeout(() => {
        handleSaveToGoogleDrive(true, 'script');
        setAutoSavedScript(true);
      }, 500);
    }
  }, [script, autoSavedScript]);

  // Auto-save when video is generated
  useEffect(() => {
    if (videoUrl && !autoSavedVideo) {
      handleSaveToGoogleDrive(true, 'video');
      setAutoSavedVideo(true);
    }
  }, [videoUrl, autoSavedVideo]);

  const handleSaveToGoogleDrive = async (isAutoSave = false, saveType: 'script' | 'video' | 'manual' = 'manual') => {
    setSaveStatus({ type: null, message: '' });
    
    const data = {
      topic,
      length: videoLength,
      structure,
      script,
      timestamp: new Date().toISOString(),
      videoUrl,
    };

    const success = await StorageService.saveToGoogleDrive(data);
    
    if (success) {
      let message = '✅ Saved to Google Drive!';
      if (isAutoSave) {
        if (saveType === 'script') {
          message = '✅ Script automatically saved to Google Drive!';
        } else if (saveType === 'video') {
          message = '✅ Video info updated and saved to Google Drive!';
        }
      }
      setSaveStatus({ type: 'success', message });
    } else {
      setSaveStatus({ type: 'error', message: 'Failed to save to Google Drive' });
    }
    
    setTimeout(() => setSaveStatus({ type: null, message: '' }), 5000);
  };

  const handleDownload = (format: 'json' | 'md') => {
    const data = {
      topic,
      length: videoLength,
      structure,
      script,
      timestamp: new Date().toISOString(),
      videoUrl,
    };

    switch (format) {
      case 'json':
        StorageService.exportAsJSON(data);
        setSaveStatus({ type: 'success', message: 'JSON file downloaded!' });
        break;
      case 'md':
        StorageService.exportAsMarkdown(data);
        setSaveStatus({ type: 'success', message: 'Markdown file downloaded!' });
        break;
    }
    
    setTimeout(() => setSaveStatus({ type: null, message: '' }), 3000);
  };

  if (!script) return null;

  return (
    <div className="mt-6 bg-slate-800/60 p-6 rounded-2xl border border-slate-700">
      <h3 className="text-xl font-bold text-white mb-4">💾 Save Options</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Google Drive Save (Main Feature) */}
        <button
          onClick={() => handleSaveToGoogleDrive(false)}
          className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-all transform hover:scale-105 shadow-lg"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.71 3.52L1.15 15l3.43 5.48L11.14 9l-3.43-5.48zm6.36 10.48l-3.43 5.48h13.72L21 14l-6.93-.02zM22.28 14L15.42 2H8.58l6.86 12h6.84z"/>
          </svg>
          <span className="font-semibold">Save to Google Drive (HTML)</span>
        </button>

        {/* Download as JSON */}
        <button
          onClick={() => handleDownload('json')}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
          </svg>
          JSON Download
        </button>

        {/* Download as Markdown */}
        <button
          onClick={() => handleDownload('md')}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Markdown Download
        </button>
      </div>

      {/* Status Message */}
      {saveStatus.type && (
        <div className={`mt-4 p-3 rounded-lg ${
          saveStatus.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {saveStatus.message}
        </div>
      )}

      {/* Auto-save Info */}
      <div className="mt-4 space-y-2">
        {autoSavedScript && (
          <div className="p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <p className="text-blue-400 text-sm">
              📝 <strong>Auto-Save Script:</strong> 
              Script generated and automatically saved to Google Drive.
            </p>
          </div>
        )}
        
        {autoSavedVideo && (
          <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
            <p className="text-green-400 text-sm">
              🎥 <strong>Video Update:</strong> 
              Video info added and updated in Google Drive.
            </p>
          </div>
        )}
      </div>

      {/* Google Drive Info */}
      <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
        <p className="text-slate-400 text-sm">
          💡 <strong>Auto-Save Feature:</strong> 
          Scripts are automatically saved to Google Drive as HTML files when generated. 
          Files are updated when videos are created.
        </p>
      </div>
    </div>
  );
};

export default SaveOptions;